import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { sortByKey, toDollar } from 'utils'
import { useReferrerState } from 'hooks'
import { useTagProduct } from 'hooks/api/useTagProduct'
import { useTagProducts, type TagShopProductVariant } from 'hooks/api/useTagProducts'
import { useCart, buildCartProductId, CUSTOMIZATION_SETUP_FEE, CUSTOMIZATION_MIN_QTY } from 'contexts/CartContext'
import {
  onMetalSurcharge, bestForForCombo, capabilityDescription, capabilityUpgradeLeaf,
  capabilityLeaves, readRange, sizeToShape, LEAF_LABEL, LEAF_BLURB, CARD_METAL_SURCHARGE_PER_UNIT,
  isCapabilityComingSoon, isLeafComingSoon, isFormComingSoon, comingSoonReason,
  EXTRA_DURABLE_SURCHARGE_PER_UNIT, isFabricForm, tagModelAsset,
  type CapabilityLeaf, type TagCapability, type TagFormFactor,
} from 'constants/tagCatalog'
import { ROUTES } from 'routes'

import {
  ChevronDown, ShoppingCartIcon, ShoppingBagIcon, PlusIcon, MinusIcon, CheckIcon, TagIcon, MoreVertical, ClockIcon, XIcon,
  NfcIcon, ShieldCheckIcon, ShieldAlertIcon, RadarIcon, KeyRoundIcon, ThermometerIcon, DropletsIcon, GaugeIcon, RadioIcon,
} from 'lucide-react'

// Atomic capability leaf → icon (VRG-156 multi-select toggles; matches the badge icons).
const LEAF_ICON: Record<CapabilityLeaf, React.ComponentType<{ size?: number; strokeWidth?: number }>> = {
  connect: NfcIcon,
  verify: ShieldCheckIcon,
  vault: KeyRoundIcon,
  tamper: ShieldAlertIcon,
  trace: RadarIcon,
  temperature: ThermometerIcon,
  moisture: DropletsIcon,
  pressure: GaugeIcon,
  proximity: RadioIcon,
}

// Canonical render order for the atomic capability chips (VRG-156): Connect → auth
// (Verify, Vault) → Trace → Sense (Tamper, Temperature, Moisture, Pressure) — same as
// the card badges' LEAF_ORDER.
const ATOM_ORDER: CapabilityLeaf[] = [
  'connect', 'verify', 'vault', 'trace', 'tamper', 'temperature', 'moisture', 'pressure', 'proximity',
]
// The Sticker Tag's 28mm size mounts behind a watch dial (decision 2026-08-03) — on-metal
// auto-selects on it since a watch dial is always metal.
const WATCH_DIAL_SIZE = '28mm'
import { alpha, useTheme, Stack, Typography, Grid, AccordionDetails, AccordionSummary, Accordion, Badge, Box, IconButton, Menu, MenuItem, Tooltip, Divider } from '@mui/material'

import { Button, Breadcrumbs, Image, Modal, MENU_PAPER_SX, MENU_ITEM_SX } from '@repo/ui'
import { CategoryList, Slideshow, TagsShopProductPreview, SetProductsForTagModal, TagCapabilityBadges, leafColors, ReferrerBackLink, ComingSoonNote } from 'components'
import { PermissionGate } from 'components/access'
import { useVeraProductsByTag, type VeraProductForTag } from 'hooks/api/useTagInventory'
import { useProductDefaultTagMutations } from 'hooks/api/useProductDefaultTag'
import { CustomizeTagModal, type TagCustomization } from './components/CustomizeTagModal'
import { TagModelSlide } from './components/TagModelSlide'
import { useTagCustomizationLibrary } from './useTagCustomizationLibrary'
import { TagDesignPreview } from './components/TagDesignPreview'

// VRG-145 — capability guide content (shown in the "Learn more about capabilities"
// modal). Never mentions the chip; describes what each capability DOES.
// One badge per capability (the single leaf it describes) + a plain-language blurb.
// Never mentions the chip. Connect is covered in the modal intro (the baseline).
// Ordered by family: authenticity (Verify, Vault), then Trace, then the sensing
// capabilities (Tamper, Temperature, Moisture). Connect (baseline) is in the intro.
const CAPABILITY_GUIDE: { leaf: CapabilityLeaf; blurb: string }[] = [
  { leaf: 'verify', blurb: "Verifies an item's authenticity and ownership. Every tap confirms it." },
  { leaf: 'vault', blurb: 'A secure ownership and provenance record. For high-value resale like watches, jewelry, art, and cars.' },
  { leaf: 'trace', blurb: 'Warehouse-scale tracking: a tap for people, an RFID read for the warehouse. For supply chain, warranty, and chain-of-custody.' },
  { leaf: 'tamper', blurb: 'A tamper-evident seal. Opening it breaks a loop and is recorded permanently. For sealed goods like wine, spirits, pharma, and cosmetics.' },
  { leaf: 'temperature', blurb: 'Senses temperature for cold-chain. For perishables like wine, gourmet food, and pharma.' },
  { leaf: 'moisture', blurb: 'Senses humidity and freshness. For coffee and cigars.' },
]

// VRG-156 — per-section configurator copy (item 5): a line under each control that
// describes the CURRENTLY-selected option (swaps on toggle; always present, incl. the
// default). Full column width, no early wrap. Durability/embroidery/material live here
// inline, NOT folded into the product Description.
const DURABILITY_TEXT: Record<string, string> = {
  'Standard': 'Soft washable woven. About 100 domestic washes, ~0.6mm, luxury feel.',
  'Extra-Durable': 'PPS/IP68 capsule. 200+ industrial washes (~120°C), ~2.5mm. Adds a surcharge.',
}
const CARD_MATERIAL_TEXT: Record<string, string> = {
  'Plastic': 'Standard plastic ID-1 card.',
  'Metal': 'Premium metal construction. Adds a surcharge.',
}
// Solid form — adhesive is an optional configurator selection (not a variant axis).
// WITH adhesive the disc is stuck flat, so it reads one-sided and the on-metal
// ferrite add-on becomes available. WITHOUT adhesive it stays a two-sided disc and
// on-metal is not offered (nothing to adhere to metal).
const ADHESIVE_OPTIONS = ['No adhesive', 'Adhesive'] as const
const ADHESIVE_TEXT: Record<string, string> = {
  'No adhesive': 'A two-sided rigid disc. Hang it, sit it, or fix it however you like.',
  'Adhesive': 'Sticks flat to the surface, so it reads one-sided. Adds the on-metal (ferrite) option for metal surfaces.',
}
// FabricPatch form — construction is an optional configurator selection (not a variant
// axis). Woven is thin and can be Standard or Extra-Durable. Embroidery is thicker and is
// always Extra-Durable. FabricLabel is woven only, so it has no construction choice.
const FABRIC_CONSTRUCTION_OPTIONS = ['Woven', 'Embroidery'] as const
const CONSTRUCTION_TEXT: Record<string, string> = {
  'Woven': 'Thin woven patch. Soft, low-profile, and flexible against the surface.',
  'Embroidery': 'Thicker embroidered patch with raised stitching. Always Extra-Durable.',
}

/**
 * One assigned-product row — mirrors the product page's DefaultTagProductInfo card
 * (image · name · secondary · kebab), but for the inverse relationship: the Vera
 * products that use THIS tag as their default. Kebab → remove that product's tag.
 */
const AssignedProductRow = ({
  product,
  onRemove,
}: {
  product: VeraProductForTag
  onRemove: () => void
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const hasImage = product.images && product.images.length > 0

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary[100]}`,
        borderRadius: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ border: `1px solid ${theme.palette.primary[100]}`, borderRadius: 2, flexShrink: 0 }}>
          {hasImage ? (
            <Image src={product.images[0]} alt={product.title || ''} fit="cover" sx={{ borderRadius: 2, width: 48, height: 48 }} />
          ) : (
            <Box
              sx={{
                borderRadius: 2,
                width: 48,
                height: 48,
                backgroundColor: theme.palette.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                No image
              </Typography>
            </Box>
          )}
        </Box>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {product.title}
          </Typography>
          {product.sku && (
            <Typography variant="body3" color="text.secondary" noWrap>
              {product.sku}
            </Typography>
          )}
        </Stack>
      </Stack>

      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: theme.palette.text.secondary }}>
        <MoreVertical size={20} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        // House menu chrome: the 8px trigger gap, the 4px paper inset, the matching
        // list inset (MUI's `MuiMenu.list` override does not reach a Popover-based
        // menu) and the viewport-safe max height. This menu opted into none of it.
        slotProps={{ paper: { sx: MENU_PAPER_SX } }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            onRemove()
          }}
          sx={{ ...MENU_ITEM_SX, color: theme.palette.error.main, fontSize: '0.875rem' }}
        >
          Remove
        </MenuItem>
      </Menu>
    </Box>
  )
}


export const TagsShopProductPage = () => {
  // Route param is the product SKU (the storefront grid links by sku).
  const { id: sku } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  // Detail → detail: the derived label would be this product's own SKU, not the shop.
  const referrer = useReferrerState('Shop')
  const { data: product, isLoading } = useTagProduct({ sku: sku as string })
  const { items: cartItems, itemCount, addItem } = useCart()

  // Similar products: prefer shared best_for_product_types tags or same category_type,
  // fall back to the first items from the store.
  const { data: allProductsData } = useTagProducts({ limit: 20 })
  const similar_products = React.useMemo(() => {
    if (!allProductsData?.products || !product) {
      return allProductsData?.products?.slice(0, 4) ?? []
    }
    const others = allProductsData.products.filter(p => p.id !== product.id)

    // Score by shared best_for_product_types entries only. category_type is 'Tags' for EVERY tag
    // product, so it can't distinguish similarity — dropping it. Require ≥2 shared
    // product types so a single incidental overlap (e.g. Woven & Solid both list "Sporting
    // Goods") doesn't surface a metal disc as "similar" to an apparel patch. Fewer than
    // 4 (or none) is fine — never pad with unrelated forms.
    const scored = others.map(p => ({
      product: p,
      score: (p.best_for_product_types || []).filter(t => (product.best_for_product_types || []).includes(t)).length,
    }))

    const withScore = scored.filter(s => s.score >= 2).sort((a, b) => b.score - a.score).map(s => s.product)

    return withScore.slice(0, 4)
  }, [allProductsData, product])

  // Initialize state hooks before any early returns
  const [selectedVariant, setSelectedVariant] = useState<TagShopProductVariant | null>(null)
  // VRG-141 configurator axes (Capability · Size); Quantity is selectedVariant.quantity.
  const [selCap, setSelCap] = useState<string | null>(null)
  const [selSize, setSelSize] = useState<string | null>(null)
  // How many of the selected pack to order (1 = one case of the chosen size).
  const [packCount, setPackCount] = useState(1)
  const [openAssignModal, setOpenAssignModal] = useState(false)
  const [openCustomize, setOpenCustomize] = useState(false)
  // VRG-145 — capability guide modal (entry point next to the Capability badges).
  const [openCapabilityGuide, setOpenCapabilityGuide] = useState(false)
  const [customization, setCustomization] = useState<TagCustomization | null>(null)
  // VRG-156 — the brand's saved-customization library (reusable logos + per-tag designs).
  const { logos: savedLogos, saveLogo, designsForForm, saveDesign } = useTagCustomizationLibrary()
  // Saved-designs dropdown on the Customization button: if this tag form has saved
  // designs, clicking opens a menu to apply one or start a new customization.
  const [customizeMenuAnchor, setCustomizeMenuAnchor] = useState<null | HTMLElement>(null)
  // VRG-142 On-Metal (ferrite) add-on — a per-unit surcharge, gated on the product.
  const [onMetal, setOnMetal] = useState(false)
  // VRG-156 configurator selections (per-unit surcharge + SKU suffix, not variant axes):
  //   Card construction (Plastic default / Metal +premium),
  //   Woven durability (Standard default / Extra-Durable +PPS).
  // (Woven is embroidered by construction — no Plain/Embroidered selection.)
  const [cardMaterial, setCardMaterial] = useState<string>('Plastic')
  const [durability, setDurability] = useState<string>('Standard')
  // VRG-246 Feature 1 — Solid adhesive (optional). Default false = no adhesive =
  // two-sided disc, on-metal NOT offered. Adhesive = stuck flat = one-sided + on-metal.
  const [adhesive, setAdhesive] = useState(false)
  // VRG-246 Feature 2 — FabricPatch construction. Woven (default) = thin, Std/XD;
  // Embroidery = thicker, always Extra-Durable.
  const [construction, setConstruction] = useState<'Woven' | 'Embroidery'>('Woven')

  // Vera products that use THIS tag as their default (inverse of the product page).
  const { data: assignedData, refetch: refetchAssigned } = useVeraProductsByTag(
    { tagShopProductId: product?.id ?? '' },
    { enabled: !!product?.id },
  )
  const assignedProducts = assignedData?.products ?? []
  const { removeDefaultTag } = useProductDefaultTagMutations({
    onSuccess: () => {
      refetchAssigned()
    },
  })

  // Update selected variant when product loads. With the capability model, default
  // to the first capability, its first size, cheapest qty.
  useEffect(() => {
    const variants = product?.variants ?? []
    if (!variants.length) return
    const hasCaps = variants.some(v => v.capability)
    if (hasCaps) {
      const caps = [...new Set(variants.map(v => v.capability).filter(Boolean))] as string[]
      // Never default to a capability we can't fulfil: the page would open pre-configured
      // to an unbuyable SKU with Add to cart live. Falls back to caps[0] only if EVERY
      // capability is coming soon, in which case the add-to-cart guard below stops it.
      const cap = caps.find(c => !isCapabilityComingSoon(c as TagCapability)) ?? caps[0]
      const size = variants.find(v => v.capability === cap)?.size ?? null
      const cheapest = variants
        .filter(v => v.capability === cap && v.size === size)
        .sort((a, b) => a.quantity - b.quantity)[0]
      setSelCap(cap)
      setSelSize(size ?? null)
      setSelectedVariant(cheapest ?? variants[0])
    } else {
      setSelectedVariant(variants[0])
    }
    setPackCount(1)
  }, [product])

  // Drop an existing customization if the customer moves DOWN to a case below the
  // minimum. Without this the button just greys out while the design stays attached --
  // so the setup fee stays in the total and the order still reaches fulfillment carrying
  // artwork the converter will refuse to run.
  //
  // MUST stay above the early returns below: it is the last hook in this component, so
  // declaring it after `if (isLoading) return` made it appear only on the loaded render
  // ("Rendered more hooks than during the previous render" -- the page threw on every
  // open). Hooks run unconditionally; the quantity is re-read inside instead.
  useEffect(() => {
    if (customization && (selectedVariant?.quantity ?? 0) < CUSTOMIZATION_MIN_QTY) {
      setCustomization(null)
    }
  }, [selectedVariant, customization])

  if (isLoading) {
    // Render-when-ready (VRG-156): the global TopProgressBar is the loading signal, so
    // this page renders nothing until its data lands, then the whole thing appears in
    // place — no bespoke skeleton that has to stay pixel-matched to the layout (which
    // otherwise rendered narrow, then snapped to the resolved grid widths).
    return null
  }

  if (!product) {
    return <div>Product not found</div>
  }

  // Variants sorted highest qty (cheapest unit price) last → ascending price
  const variantsByPrice = sortByKey(product.variants, 'price')

  // ── VRG-266 gallery: product hero + the SELECTED VARIANT's image ─────────────
  // The gallery was product-level only (`images={product?.images}`), so choosing a
  // different quantity never changed the picture — the packaging shot could not
  // surface at all, and with it the box-size cue that is the whole point of the
  // package layer.
  //
  // Order is deliberate: the product's first image stays the hero (the spinning tag
  // GIF, which carries the tag's identity), the variant's packaging shot slots in at
  // position 2, then the rest of the product gallery. The variant image is removed
  // from `rest` first so it cannot appear twice.
  //
  // Plain consts, not useMemo: there are early returns above this line, so a hook
  // here would violate the rules of hooks. The work is a filter over a handful of
  // strings, so memoising buys nothing anyway.
  //
  // `images[0]`, NOT `.image`: a variant carries `images: string[]` (see
  // `TagShopProductVariant`), and the API sets it to `[variant.image.url]` — falling
  // back to the product's featured image when Shopify has no per-variant upload. The
  // first shipped version of this read a non-existent `.image` scalar behind an
  // `as any`, so it was always null and the whole feature was inert; the cast is what
  // hid it from the compiler. No cast here, deliberately — the type is the guard.
  //
  // The fallback is why the dedupe below matters: when a variant has no image of its
  // own its entry EQUALS the product hero, so without the filter the same picture
  // would render twice and the gallery would look broken rather than merely unchanged.
  const selectedVariantImage = selectedVariant?.images?.[0] ?? null
  const galleryImages: string[] | null = (() => {
    const productImages = product.images ?? []
    if (!selectedVariantImage) {
      // Preserve the loading contract: [] means "loaded, no images" (empty state),
      // null/undefined means "still loading" (skeleton). Never conflate them.
      return productImages
    }
    // Already the hero — nothing to insert. This is the COMMON case today, because the
    // API falls back to the product's featured image for any variant Shopify has no
    // upload for. Returning early matters: the general path below rebuilds the array
    // around the variant image, which for the hero would demote it to slot 2 and
    // promote image 2 into its place. A quantity change must never reshuffle the
    // gallery of a product that has no per-variant photography.
    if (selectedVariantImage === productImages[0]) return productImages

    // Hero stays first (it carries the tag's identity), the variant's packaging shot
    // slots in at 2, then the remainder. Filtered out of the tail so it cannot appear
    // twice.
    const [hero, ...tail] = productImages
    const rest = tail.filter(i => i !== selectedVariantImage)
    return [hero, selectedVariantImage, ...rest].filter(Boolean) as string[]
  })()

  // ── VRG-141 configurator (Capability · Size · Quantity) ──────────────────────
  const hasCapabilityModel = product.variants.some((v: any) => v.capability)
  const caps: string[] = [...new Set(product.variants.map((v: any) => v.capability).filter(Boolean))]
  const sizesForCap: string[] = [
    ...new Set(product.variants.filter((v: any) => v.capability === selCap).map((v: any) => v.size)),
  ]
  const qtysForCapSize = product.variants
    .filter((v: any) => v.capability === selCap && v.size === selSize)
    .sort((a: any, b: any) => a.quantity - b.quantity)

  // ── VRG-156 atomic multi-select capability model ─────────────────────────────
  // The combo (`selCap`) stays the source of truth (drives size-gating + price). The
  // atomic chips are a VIEW over it: Connect is the always-on baseline; Verify is the
  // auth floor; a tag carries AT MOST ONE upgrade on top of Verify. Selecting an
  // upgrade resolves to that upgrade's combo; the other upgrades disable until cleared.
  const form = (product.form_factor ?? null) as TagFormFactor | null
  const comboByUpgrade = new Map<CapabilityLeaf, TagCapability>()
  let verifyCombo: TagCapability | null = null
  for (const c of caps as TagCapability[]) {
    const up = capabilityUpgradeLeaf(c)
    if (up) comboByUpgrade.set(up, c)
    else if (capabilityLeaves(c).includes('verify')) verifyCombo = c // the 'Verify' combo
  }
  const hasVerify = (caps as TagCapability[]).some(c => capabilityLeaves(c).includes('verify'))
  const activeUpgrade = selCap ? capabilityUpgradeLeaf(selCap as TagCapability) : null
  // Atoms to render, in canonical order: connect (baseline) + verify (if offered) + present upgrades.
  const atomChips = ATOM_ORDER.filter(a =>
    a === 'connect' || (a === 'verify' && hasVerify) || comboByUpgrade.has(a),
  )
  // Clicking an atom resolves to a combo (or clears an upgrade back to Verify).
  const pickAtom = (atom: CapabilityLeaf) => {
    if (atom === 'connect') return // baseline — never toggleable
    if (atom === 'verify') {
      if (verifyCombo) pickCapability(verifyCombo) // clears any active upgrade
      return
    }
    if (activeUpgrade && atom !== activeUpgrade) return // incompatible — disabled
    if (atom === activeUpgrade) {
      if (verifyCombo) pickCapability(verifyCombo) // unclick the upgrade → back to Verify
      return
    }
    const combo = comboByUpgrade.get(atom)
    if (combo) pickCapability(combo)
  }

  // Best-for list reflects the SELECTED combo. PRODUCT TYPE grain, not category:
  // `bestForForCombo` is the (form, capability) intersection at type grain, and the
  // fallback is the `best_for_product_types` column. Hence the name — this list is
  // NOT categories, and calling it that is what made the two words drift (VRG-628).
  const comboBestFor = form && selCap ? bestForForCombo(form, selCap as TagCapability) : []
  const bestForProductTypes = comboBestFor.length ? comboBestFor : (product.best_for_product_types || [])

  // VRG-156 — dynamic Description: composed from the selected capability's effect plus
  // the active add-on / customization notes. Recomputed on any selection change.
  // Description stays product + capability (+ add-ons). Durability/embroidery/material
  // are NOT folded in — they surface inline under their own controls (VRG-156 item 5).
  const descriptionNotes = [
    selCap ? capabilityDescription(selCap as TagCapability) : null,
    onMetal && product.on_metal_available
      ? 'On-metal (ferrite) construction protects the tag from metal interference.'
      : null,
    customization ? 'Includes your custom design.' : null,
  ].filter(Boolean) as string[]

  const pickCapability = (cap: string) => {
    const size = product.variants.find((v: any) => v.capability === cap)?.size ?? null
    const cheapest = product.variants
      .filter((v: any) => v.capability === cap && v.size === size)
      .sort((a: any, b: any) => a.quantity - b.quantity)[0]
    setSelCap(cap)
    setSelSize(size)
    setSelectedVariant(cheapest ?? null)
    setPackCount(1)
  }
  const pickSize = (size: string) => {
    const cheapest = product.variants
      .filter((v: any) => v.capability === selCap && v.size === size)
      .sort((a: any, b: any) => a.quantity - b.quantity)[0]
    setSelSize(size)
    setSelectedVariant(cheapest ?? null)
    setPackCount(1)
    // VRG-142 (decision 2026-08-03): 28mm is the watch-dial mount — a watch dial is
    // always metal, so on-metal (ferrite) auto-selects and isn't left opt-in.
    if (size === WATCH_DIAL_SIZE && product.on_metal_available) setOnMetal(true)
  }
  // The 28mm Sticker size mounts behind a watch dial (see decisions/LOG.md).
  const isWatchDialSize = selSize === WATCH_DIAL_SIZE && product.on_metal_available

  // VRG-246 Feature 1 — Solid adhesive. Switching back to No adhesive drops the
  // on-metal add-on (a bare two-sided disc has nothing to adhere to metal).
  const pickAdhesive = (val: boolean) => {
    setAdhesive(val)
    if (!val) setOnMetal(false)
  }
  // VRG-246 Feature 2 — FabricPatch construction. Embroidery is always Extra-Durable,
  // so selecting it forces the durability grade and locks the Standard chip below.
  const pickConstruction = (val: 'Woven' | 'Embroidery') => {
    setConstruction(val)
    if (val === 'Embroidery') setDurability('Extra-Durable')
  }
  // Embroidery locks the Fabric durability to Extra-Durable (FabricPatch only).
  const lockExtraDurable = form === 'FabricPatch' && construction === 'Embroidery'

  // Price-per-unit savings: compare selectedVariant.unit_price vs the most
  // expensive per-unit variant (smallest case = highest unit_price).
  const maxUnitPriceVariant = product.variants.reduce(
    (max, v) => (v.unit_price > max.unit_price ? v : max),
    product.variants[0]
  )
  // The largest pack tier (highest quantity). The tiers already cover the normal
  // range, so the multiplier only makes sense here — to order beyond the top tier.
  const maxQtyVariant = product.variants.reduce(
    (max, v) => (v.quantity > max.quantity ? v : max),
    product.variants[0]
  )
  const isMaxTierSelected =
    !!selectedVariant && !!maxQtyVariant && selectedVariant.quantity === maxQtyVariant.quantity
  const savingsPct =
    selectedVariant &&
    maxUnitPriceVariant &&
    selectedVariant.unit_price < maxUnitPriceVariant.unit_price
      ? Math.round(
          ((maxUnitPriceVariant.unit_price - selectedVariant.unit_price) /
            maxUnitPriceVariant.unit_price) *
            100
        )
      : null

  // Selectable configurator chip styling (brand tint + border when selected).
  // position: relative so the "selected" check can sit in the top-right corner
  // without shifting the centered label.
  const chipSx = (selected: boolean, disabled = false) => ({
    position: 'relative' as const,
    px: 3,
    mr: 2,
    mt: 2,
    cursor: disabled ? 'default' : 'pointer',
    borderRadius: 2,
    opacity: disabled ? 0.5 : 1,
    border: `1px solid ${selected ? theme.palette.brand[500] : theme.palette.grey[300]}`,
    backgroundColor: selected ? alpha(theme.palette.brand[500], 0.1) : undefined,
    '&:hover': disabled
      ? {}
      : { backgroundColor: alpha(theme.palette.brand[500], 0.1), borderColor: theme.palette.brand[500] },
  })

  // VRG-156 atomic-capability chip styling — coloured by capability FAMILY so a selected
  // chip matches the card TagCapabilityBadges exactly (auth=purple, trace=blue, sense=orange,
  // connect=neutral grey). `family` resolves the fill/border/text stops via familyColors
  // (the single source the badges use). `selected` = included (family tint+border+text+check);
  // `disabled` = incompatible with the active upgrade (dimmed, non-clickable);
  // `interactive` = clickable (baseline Connect / locked Verify are selected but not interactive).
  const atomChipSx = (opts: {
    selected: boolean; disabled: boolean; interactive: boolean; family: CapabilityLeaf
  }) => {
    const c = leafColors(opts.family, theme.palette.mode === 'dark')
    return {
      position: 'relative' as const,
      px: 3,
      mr: 2,
      mt: 2,
      cursor: opts.interactive ? 'pointer' : 'default',
      borderRadius: 2,
      opacity: opts.disabled ? 0.4 : 1,
      // Selected chip inherits the family text stop; children (icon, label, check) pick
      // it up via currentColor. Unselected chips keep the default text colour.
      color: opts.selected ? c.text : undefined,
      border: `1px solid ${opts.selected ? c.border : theme.palette.grey[300]}`,
      backgroundColor: opts.selected ? c.fill : undefined,
      '&:hover': opts.interactive
        ? { backgroundColor: c.fill, borderColor: c.border, color: c.text }
        : {},
    }
  }

  // Selected check — rendered inline to the RIGHT of the chip label. White in dark
  // mode (reads on the brand tint), brand-coloured in light mode.
  const SelectedCheck = () => (
    <CheckIcon
      size={15}
      strokeWidth={2.5}
      style={{
        flexShrink: 0,
        color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.brand[600],
      }}
    />
  )

  // VRG-156 per-unit configurator surcharge + SKU suffix. Baked into the cart's
  // unit/case price (the cart layer stays unaware of these selections). Card metal +
  // Fabric-form durability joins the same expression. (Fabric forms are embroidered by construction —
  // embroidery is baseline, not a surcharge option.)
  const round2 = (n: number) => Math.round(n * 100) / 100
  const configUnitSurcharge =
    (form === 'Card' && cardMaterial === 'Metal' ? CARD_METAL_SURCHARGE_PER_UNIT : 0) +
    (isFabricForm(form) && durability === 'Extra-Durable' ? EXTRA_DURABLE_SURCHARGE_PER_UNIT : 0)
  const configSkuSuffix = [
    form === 'Card' && cardMaterial === 'Metal' ? 'METAL' : null,
    isFabricForm(form) && durability === 'Extra-Durable' ? 'XD' : null,
    // Embroidery construction (FabricPatch). It rides Extra-Durable for pricing (no
    // separate surcharge), but the SKU records the woven-vs-embroidered choice.
    form === 'FabricPatch' && construction === 'Embroidery' ? 'EMB' : null,
  ].filter(Boolean).join('-')

  /*
   * VRG-780 — THE MODEL IS THE PRODUCT'S FIRST PICTURE, not an extra one.
   *
   * Keyed on the appearance the buyer has ACTUALLY SELECTED, which is the whole
   * difference between this and the grid. The grid is product-level: one tile per
   * form, so the only appearance it can name is the form's representative one
   * (`primaryTagModel`). Here a size and a build have been chosen, so the model
   * follows them — pick the 45x25mm patch and the 45x25mm patch is what turns.
   *
   * ⚠️ NO FALLBACK TO THE FORM'S REPRESENTATIVE MODEL when the exact appearance
   * has none. That would spin a 25x25mm patch for a buyer looking at the 45x25mm
   * one, which is the lie `primaryTagModel`'s docblock refuses in the other
   * direction. A different object is a worse answer than no object, so the slide
   * is simply absent and the stills carry the gallery.
   *
   * `tagModelAsset` answers EXISTENCE before returning a URL, so an unmodelled
   * appearance mounts no viewer at all rather than fetching model-viewer and then
   * reporting a failure on a surface where nothing was promised.
   *
   * Codes are the IMAGERY vocabulary (`BUILD_OPTIONS`), not `configSkuSuffix`'s —
   * those two disagree on embroidery (`EMBR` here, `EMB` in the SKU) and reusing
   * the SKU string would resolve a filename that has never existed.
   */
  const appearanceOptions: string[] = form
    ? ([
        form === 'Card' && cardMaterial === 'Metal' ? 'METAL' : null,
        isFabricForm(form) && durability === 'Extra-Durable' ? 'XD' : null,
        form === 'FabricPatch' && construction === 'Embroidery' ? 'EMBR' : null,
      ].filter(Boolean) as string[])
    : []
  const tagModelSrc = form && selSize
    ? tagModelAsset({form, size: selSize, options: appearanceOptions})
    : undefined

  // Belt-and-braces against ordering something we can't ship. The selector already
  // refuses to select a coming-soon capability and the default pick skips them, but a
  // Shopify variant carrying one (they are seeded on purpose, to stay visible) could
  // still be reached via a deep link to its SKU, so the buy actions check too.
  //
  // ⚠️ VRG-630 — TWO GRAINS, AND THE FORM ONE HAS NO ESCAPE HATCH. A coming-soon
  // CAPABILITY can be stepped off: the selector offers other rungs of the same
  // ladder. A coming-soon FORM cannot — every variant of it is unbuyable, whatever
  // the buyer selects — so `formComingSoon` is not derived from the selection and
  // does not clear when the selection changes. `ClosureLoop` is the case: its lone
  // `Verify + Tamper` rung is perfectly orderable on `Closure`, so a
  // capability-only check would leave this product's Add to cart live.
  // `form` is `TagFormFactor | null` (line ~390: the Shopify metafield may be absent),
  // so it is narrowed here the same way `selCap` is below. A product whose form we cannot
  // read is NOT treated as coming-soon: the honest default for an unknown form is the
  // existing behaviour, and blocking checkout on a missing metafield would turn a data gap
  // into a lost order.
  const formComingSoon = form ? isFormComingSoon(form) : false
  const selectedCapComingSoon = selCap ? isCapabilityComingSoon(selCap as TagCapability) : false
  const offerComingSoon = formComingSoon || selectedCapComingSoon

  const handleAddToCart = () => {
    if (offerComingSoon) return
    if (!selectedVariant) return
    const unitsPerCase = selectedVariant.quantity
    const baseSku = selectedVariant.sku ?? product.sku
    addItem({
      productId: buildCartProductId(product.id, selectedVariant.id),
      variantId: selectedVariant.id,
      sku: configSkuSuffix ? `${baseSku}-${configSkuSuffix}` : baseSku,
      title: product.title,
      variantTitle: selectedVariant.title,
      unitPrice: round2(selectedVariant.unit_price + configUnitSurcharge),
      casePrice: round2(selectedVariant.price + configUnitSurcharge * unitsPerCase),
      quantity: packCount,
      unitsPerCase,
      onMetal: onMetal || undefined,
      image: product.images?.[0],
      customization: customization ?? undefined,
    })
  }

  // On-metal per-unit surcharge for the current selection (shown in the price).
  const onMetalCost =
    onMetal && selectedVariant ? onMetalSurcharge(selectedVariant.quantity * packCount) : 0
  // Configurator (material/durability/embroidery) surcharge total for the current line.
  const configCost = selectedVariant
    ? round2(configUnitSurcharge * selectedVariant.quantity * packCount)
    : 0
  // Customization = an artwork/plate setup fee, charged once PER ARTWORK — not per tag,
  // not per line, and not again on a reorder of the same design (VRG-575; this comment
  // said "ONCE per line" until then, which is what the code did and what the ticket
  // removed). Shown in the price breakdown like on-metal (VRG-156).
  //
  // The figure below is a single-line ESTIMATE for this page only. It cannot know whether
  // the company has already paid setup for this design — that is a server read — so a
  // reorder shows a fee here and is not charged at checkout, where the total comes from
  // the API. Erring high is deliberate: a total that GROWS at the card form is the one
  // customers experience as a bait and switch.
  // Customization has a 1000-unit minimum: the setup fee covers artwork/plate
  // origination, which the converter will not run for a small case. Shown DISABLED with
  // a tooltip rather than hidden, so the option stays discoverable and the requirement is
  // stated -- a customer who wants it learns the threshold instead of wondering where it
  // went (working-conventions §27: disable+tooltip for in-context actions, hide for
  // whole surfaces).
  const selectedQty = selectedVariant?.quantity ?? 0
  const customizationAllowed = selectedQty >= CUSTOMIZATION_MIN_QTY
  const customizationLockReason = customizationAllowed
    ? ''
    : `Customization requires an order of ${CUSTOMIZATION_MIN_QTY.toLocaleString()} tags or more. Choose a larger case size to add your own design.`

  const customizationCost = customization ? CUSTOMIZATION_SETUP_FEE : 0
  // Saved designs for THIS tag form. If any exist, the Customization button opens a menu
  // (apply a saved design or start new); otherwise it opens the customizer directly.
  const myDesigns = designsForForm(form)
  const openCustomizeOrMenu = (e: React.MouseEvent<HTMLElement>) => {
    if (myDesigns.length) setCustomizeMenuAnchor(e.currentTarget)
    else setOpenCustomize(true)
  }

  const handleBuyNow = () => {
    handleAddToCart()
    navigate(ROUTES.TAGS_SHOP_CART)
  }

  // VRG-156 — the customization canvas shape/dimensions come from the SELECTED size,
  // not the product's coarse `shape` metafield (which reports 'rect' for any form that
  // also offers a rectangular size, drawing a round disc on a square canvas). Fall back
  // to the product metafields when a size isn't parseable (or none is selected).
  const canvasFromSize = selSize ? sizeToShape(selSize) : null

  // VRG-156 — saved-customization thumbnails shown beside the "Customized" button.
  // Same canvas shape/aspect the modal used, so the mini face matches what was placed.
  const previewShape: 'round' | 'rect' =
    (canvasFromSize?.shape ?? product?.shape) === 'round' ? 'round' : 'rect'
  const previewW = Number(canvasFromSize?.widthMm ?? product?.width) || 1
  const previewH = Number(canvasFromSize?.heightMm ?? product?.height) || 1
  const previewAspect = previewW > 0 && previewH > 0 ? previewW / previewH : 1
  // VRG-246 Feature 1 — Solid's face count depends on adhesive: stuck flat = one-sided,
  // adhesive-free = two-sided. Every other form keeps its seeded `sides`.
  const previewSides = form === 'Solid'
    ? (adhesive ? 1 : 2)
    : (Number(product?.sides) === 2 ? 2 : 1)
  // Back mirrors the front until the buyer customises it (draft.back === null).
  const previewBack = customization
    ? customization.back ?? {
        logoDataUrl: customization.logoDataUrl,
        logoName: customization.logoName,
        logoPlacement: customization.logoPlacement,
      }
    : null
  const customizationThumbs = customization ? (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <TagDesignPreview
        shape={previewShape}
        aspect={previewAspect}
        tagColor={customization.color}
        embroidered={isFabricForm(form)}
        front={{ logoDataUrl: customization.logoDataUrl, logoPlacement: customization.logoPlacement }}
        back={
          previewSides === 2 && previewBack
            ? { logoDataUrl: previewBack.logoDataUrl, logoPlacement: previewBack.logoPlacement }
            : null
        }
      />
      {/* Clear the customization (X = remove a set selection, not a destructive delete). */}
      <Tooltip title="Remove customization">
        <IconButton
          size="small"
          onClick={() => setCustomization(null)}
          sx={{ color: theme.palette.text.secondary, '&:hover': { color: theme.palette.text.primary } }}
        >
          <XIcon size={16} strokeWidth={1.5} />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : null

  return (
    <>
      <Stack pt={2} direction="row" justifyContent="space-between" alignItems="center" pb={6}>
        {/* This page renders its own breadcrumb rather than the shared Header, so the
            referrer back-link is mounted explicitly (same as IntegrationPage) — otherwise
            every link INTO the shop product page would pass state nothing renders. §42 */}
        <Stack direction="row" alignItems="center" spacing={3}>
          <Breadcrumbs breadcrumbsOveride={product?.title} />
          <ReferrerBackLink />
        </Stack>
        <Stack direction="row" spacing="8px" alignItems="center">
          <Button
            startIcon={
              <Badge
                badgeContent={itemCount}
                sx={{
                  '& .MuiBadge-badge': {
                    background: 'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgba(11,10,103,1) 100%)',
                    // Fixed white — the gradient is a fixed purple in both themes, and
                    // theme.palette.white is inverted to a dark value in dark mode
                    // (making the count illegible). Matches the Tags header cart badge.
                    color: '#FFFFFF',
                  },
                }}
              >
                <ShoppingCartIcon strokeWidth={1} />
              </Badge>
            }
            variant="secondary"
            onClick={() => navigate(ROUTES.TAGS_SHOP_CART)}
          >
            Cart
          </Button>
        </Stack>
      </Stack>
      <>
        <Grid container spacing={6}>
          <Grid item xs={12} md={5}>
            {/*
              `contain` + the media plate, not `cover`. A tag plate is a cut-out on
              transparency, so `cover` zoomed past the part's own edges — the 85.6x54mm
              card rendered as a wall of black with two letters of the wordmark left in
              frame. Once the artwork no longer fills the square something has to paint
              around it, and the plate is that surface (theme/mediaPlate.ts).
            */}
            <Slideshow
              images={galleryImages}
              focusImage={selectedVariantImage}
              fit="contain"
              mediaPlate
              leadSlide={tagModelSrc ? <TagModelSlide src={tagModelSrc} title={product.title} /> : null}
              leadThumb={tagModelSrc ? galleryImages?.[0] : undefined}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={6}>
              {/* Match the standard page-title size (Header: Lato 24px/600). */}
              <Typography
                sx={{
                  fontFamily: 'Lato, Montserrat, sans-serif',
                  fontSize: '24px',
                  fontWeight: 600,
                  lineHeight: '130%',
                }}
              >
                {product.title}
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body2" noWrap>
                  Best for
                </Typography>
                <Grid container>
                  <CategoryList categories={bestForProductTypes} maxVisible={5} filter kind="productType" />
                </Grid>
              </Stack>
              {hasCapabilityModel ? (
                <Stack spacing={5}>
                  {/* Capability — the primary axis, a selectable ladder (VRG-156).
                      Selecting a capability updates the price, the available sizes, and
                      (internally) the tag_family. Every capability is orderable; the
                      chip / tag_family is never surfaced to buyers. */}
                  <Stack spacing={2}>
                    {/* Guide link (VRG-145) sits to the right of the label — a quiet
                        secondary link (matching the Best-for "+N more"). */}
                    <Stack direction="row" alignItems="center" spacing={3}>
                      <Typography variant="body2" noWrap>Capability</Typography>
                      <Button
                        variant="text"
                        onClick={() => setOpenCapabilityGuide(true)}
                        sx={{
                          minWidth: 0,
                          px: 1,
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          fontSize: 14,
                          color: 'text.secondary',
                          '&:hover': { color: 'text.primary', backgroundColor: 'transparent' },
                        }}
                      >
                        Learn more about capabilities
                      </Button>
                    </Stack>
                    <Grid container>
                      {atomChips.map(atom => {
                        const Icon = LEAF_ICON[atom]
                        // Connect: always included, never toggleable.
                        // Verify: the auth floor — included always; interactive only to
                        //   clear an active upgrade. Sense combos imply Verify (shown checked).
                        // Upgrades: selected when active; disabled when a DIFFERENT upgrade is active.
                        let selected: boolean
                        let disabled = false
                        let interactive = false
                        if (atom === 'connect') {
                          selected = true
                        } else if (atom === 'verify') {
                          selected = true
                          interactive = activeUpgrade !== null
                        } else {
                          selected = atom === activeUpgrade
                          disabled = activeUpgrade !== null && atom !== activeUpgrade
                          interactive = !disabled
                        }
                        // Capabilities whose silicon isn't in supply yet stay visible but
                        // cannot be selected -- otherwise the configurator would happily
                        // build a SKU and take an order we can't fulfil. Shown-and-disabled
                        // rather than hidden so the customer learns it's coming and how to
                        // ask for it (§27).
                        const comingSoon = isLeafComingSoon(atom)
                        if (comingSoon) {
                          selected = false
                          disabled = true
                          interactive = false
                        }
                        return (
                          // Hover any capability chip to learn what it does (VRG-156).
                          <Tooltip
                            key={atom}
                            title={
                              comingSoon ? (
                                <ComingSoonNote>{comingSoonReason(LEAF_LABEL[atom])}</ComingSoonNote>
                              ) : (
                                LEAF_BLURB[atom]
                              )
                            }
                            placement="bottom"
                            arrow
                          >
                            <Grid
                              item
                              onClick={interactive ? () => pickAtom(atom) : undefined}
                              sx={atomChipSx({ selected, disabled, interactive, family: atom })}
                            >
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, px: 1 }}>
                                {Icon && <Icon size={16} strokeWidth={1.5} />}
                                <Typography variant="body3">{LEAF_LABEL[atom]}</Typography>
                                {/* Check inherits the chip's family text colour (currentColor). */}
                                {selected && (
                                  <CheckIcon size={15} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                                )}
                              </Stack>
                            </Grid>
                          </Tooltip>
                        )
                      })}
                    </Grid>
                    {/* One-line description of the selected combination (VRG-156). */}
                    {selCap && (
                      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {capabilityDescription(selCap as TagCapability)}
                      </Typography>
                    )}
                  </Stack>
                  {/* Size — depends on the chosen capability. */}
                  <Stack spacing={2}>
                    <Typography variant="body2" noWrap>Size</Typography>
                    <Grid container>
                      {sizesForCap.map(size => {
                        const isWatch = size === WATCH_DIAL_SIZE && product.on_metal_available
                        return (
                          <Grid item key={size} onClick={() => pickSize(size)} sx={chipSx(selSize === size)}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, minWidth: 40, px: isWatch ? 1.5 : 0 }}>
                              <Typography variant="body3">{size}</Typography>
                              {isWatch && (
                                <Typography variant="body4" color="text.secondary" noWrap>· Watch dial</Typography>
                              )}
                              {selSize === size && <SelectedCheck />}
                            </Stack>
                          </Grid>
                        )
                      })}
                    </Grid>
                    {/* Scan range — antenna/size-driven; shown once a size is chosen (VRG-156). */}
                    {selCap && selSize && (
                      <Typography variant="body3" color="text.secondary">
                        Scan range: {readRange(selCap as TagCapability, selSize)}
                      </Typography>
                    )}
                    {/* Watch-dial note (VRG-142) — explains the auto-included on-metal. */}
                    {isWatchDialSize && (
                      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        Mounts behind a watch dial. On-metal (ferrite) construction is included
                        automatically — a watch dial is always metal.
                      </Typography>
                    )}
                  </Stack>
                  {/* Adhesive — Solid only (VRG-246). Adhesive → stuck flat (one-sided) +
                      on-metal option; No adhesive → two-sided disc, no on-metal. */}
                  {form === 'Solid' && (
                    <Stack spacing={2}>
                      <Typography variant="body2" noWrap>Adhesive</Typography>
                      <Grid container>
                        {ADHESIVE_OPTIONS.map(opt => {
                          const isSelected = (adhesive ? 'Adhesive' : 'No adhesive') === opt
                          return (
                            <Grid item key={opt} onClick={() => pickAdhesive(opt === 'Adhesive')} sx={chipSx(isSelected)}>
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, minWidth: 40 }}>
                                <Typography variant="body3">{opt}</Typography>
                                {isSelected && <SelectedCheck />}
                              </Stack>
                            </Grid>
                          )
                        })}
                      </Grid>
                      {/* Per-selection copy — swaps with the chosen adhesive option (VRG-246). */}
                      {ADHESIVE_TEXT[adhesive ? 'Adhesive' : 'No adhesive'] && (
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {ADHESIVE_TEXT[adhesive ? 'Adhesive' : 'No adhesive']}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {/* Card construction — Plastic (default) or Metal (+premium). VRG-156. */}
                  {form === 'Card' && (
                    <Stack spacing={2}>
                      <Typography variant="body2" noWrap>Material</Typography>
                      <Grid container>
                        {(product.cardMaterial ?? ['Plastic', 'Metal']).map(mat => (
                          <Grid item key={mat} onClick={() => setCardMaterial(mat)} sx={chipSx(cardMaterial === mat)}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, minWidth: 40 }}>
                              <Typography variant="body3">{mat}</Typography>
                              {cardMaterial === mat && <SelectedCheck />}
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                      {/* Per-selection copy — swaps with the chosen material (VRG-156). */}
                      {CARD_MATERIAL_TEXT[cardMaterial] && (
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {CARD_MATERIAL_TEXT[cardMaterial]}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {/* Construction — FabricPatch only (VRG-246). Woven = thin (Std/XD);
                      Embroidery = thicker, always Extra-Durable (locks the Standard grade). */}
                  {form === 'FabricPatch' && (
                    <Stack spacing={2}>
                      <Typography variant="body2" noWrap>Construction</Typography>
                      <Grid container>
                        {FABRIC_CONSTRUCTION_OPTIONS.map(opt => (
                          <Grid item key={opt} onClick={() => pickConstruction(opt)} sx={chipSx(construction === opt)}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, minWidth: 40 }}>
                              <Typography variant="body3">{opt}</Typography>
                              {construction === opt && <SelectedCheck />}
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                      {/* Per-selection copy — thin woven vs thicker embroidery (VRG-246). */}
                      {CONSTRUCTION_TEXT[construction] && (
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {CONSTRUCTION_TEXT[construction]}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {/* Fabric configurator — Durability (VRG-156). Fabric forms are embroidered by
                      construction (the Vera logo by default; a custom logo via
                      Customization), so there's no Plain/Embroidered selection. */}
                  {isFabricForm(form) && (
                    <Stack spacing={2}>
                      <Typography variant="body2" noWrap>Durability</Typography>
                      <Grid container>
                        {(product.durability ?? ['Standard', 'Extra-Durable']).map(grade => {
                          // Embroidery is always Extra-Durable → the Standard chip locks (VRG-246).
                          const gradeDisabled = lockExtraDurable && grade === 'Standard'
                          return (
                            <Grid
                              item
                              key={grade}
                              onClick={gradeDisabled ? undefined : () => setDurability(grade)}
                              sx={chipSx(durability === grade, gradeDisabled)}
                            >
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, minWidth: 40 }}>
                                <Typography variant="body3">{grade}</Typography>
                                {durability === grade && <SelectedCheck />}
                              </Stack>
                            </Grid>
                          )
                        })}
                      </Grid>
                      {/* Per-selection copy — swaps with the chosen durability (VRG-156). When
                          embroidery locks the grade, state why (VRG-246). */}
                      {lockExtraDurable ? (
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          Embroidery is always Extra-Durable.
                        </Typography>
                      ) : DURABILITY_TEXT[durability] ? (
                        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {DURABILITY_TEXT[durability]}
                        </Typography>
                      ) : null}
                    </Stack>
                  )}
                  {/* Quantity (case tier) + Add-ons — both content rows share the same
                      top (mt from chipSx), so the add-on buttons line up with the
                      quantity chips. */}
                  <Stack spacing={6} direction="row">
                    <Stack spacing={2}>
                      <Typography variant="body2" noWrap>Quantity</Typography>
                      <Grid container>
                        {qtysForCapSize.map((variant: any) => (
                          <Grid
                            item
                            key={variant.sku}
                            onClick={() => { setSelectedVariant(variant); setPackCount(1) }}
                            sx={chipSx(selectedVariant?.sku === variant.sku)}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 1.75, minWidth: 40 }}>
                              <Typography variant="body3">{variant.quantity}</Typography>
                              {selectedVariant?.sku === variant.sku && <SelectedCheck />}
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                    <Stack spacing={2}>
                      <Typography variant="body2" noWrap>Add-ons</Typography>
                      {/* Align with the quantity chips on both axes: height 34 matches the
                          chip height; each button carries mt:2 (8px) to match the chip's own
                          chipSx mt:2. The wrapper's own mt can't do it — MUI Stack spacing
                          injects a marginTop on this child that wins over sx. */}
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                          <Tooltip title={customizationLockReason}>
                            {/* span: MUI drops tooltips on disabled buttons (no pointer events). */}
                            <span>
                              <Button
                                variant={customization ? 'primary' : 'secondary'}
                                disableElevation
                                disabled={!customizationAllowed}
                                startIcon={customization ? <CheckIcon strokeWidth={1} /> : <PlusIcon strokeWidth={1} />}
                                onClick={openCustomizeOrMenu}
                                sx={{ height: 34 }}
                              >
                                {customization ? 'Customized' : 'Customization'}
                              </Button>
                            </span>
                          </Tooltip>
                          {/* Saved design — front (+ back) thumbnails beside the button (VRG-156). */}
                          {customizationThumbs}
                        </Stack>
                        {/* On-Metal (ferrite) — only where the product supports it. On the 28mm
                            watch-dial size it's auto-included + locked (VRG-142). For Solid it's
                            offered only WITH adhesive (a bare disc has nothing to adhere to metal). */}
                        {product.on_metal_available && (form !== 'Solid' || adhesive) && (
                          <Tooltip
                            title="Included automatically for the 28mm watch-dial size."
                            arrow
                            disableHoverListener={!isWatchDialSize}
                          >
                            <span style={{ display: 'inline-flex' }}>
                              <Button
                                variant={onMetal ? 'primary' : 'secondary'}
                                disableElevation
                                startIcon={onMetal ? <CheckIcon strokeWidth={1} /> : <PlusIcon strokeWidth={1} />}
                                onClick={() => { if (!isWatchDialSize) setOnMetal(v => !v) }}
                                disabled={isWatchDialSize}
                                sx={{ height: 34, mt: 2 }}
                              >
                                {isWatchDialSize ? 'On-metal · included' : 'On-metal'}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                      {/* On-metal explainer — only when the add-on is active (VRG-156). */}
                      {onMetal && product.on_metal_available && (
                        <Typography variant="body3" color="text.secondary" sx={{ mt: 2, maxWidth: 280, lineHeight: 1.5 }}>
                          Adds a ferrite backing to protect the tag from metal interference.
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={6} direction="row">
                  <Stack spacing={2}>
                    <Typography variant="body2" noWrap>Select quantity</Typography>
                    <Grid container>
                      {variantsByPrice.map((variant, index) => (
                        <Grid
                          item
                          key={index}
                          onClick={() => { setSelectedVariant(variant); setPackCount(1) }}
                          sx={chipSx(selectedVariant?.sku === variant.sku)}
                        >
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="body3" sx={{ py: 1.75 }}>{variant.quantity}</Typography>
                            {selectedVariant?.sku === variant.sku && <CheckIcon strokeWidth={1} />}
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                  <Stack spacing={4}>
                    <Typography variant="body2" noWrap>Design</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Tooltip title={customizationLockReason}>
                        <span>
                          <Button
                            variant={customization ? 'primary' : 'secondary'}
                            disableElevation
                            disabled={!customizationAllowed}
                            startIcon={customization ? <CheckIcon strokeWidth={1} /> : <PlusIcon strokeWidth={1} />}
                            onClick={() => setOpenCustomize(true)}
                            sx={{ height: 34 }}
                          >
                            {customization ? 'Customized' : 'Add customization'}
                          </Button>
                        </span>
                      </Tooltip>
                      {/* Saved design — front (+ back) thumbnails beside the button (VRG-156). */}
                      {customizationThumbs}
                    </Stack>
                  </Stack>
                </Stack>
              )}
              <Stack spacing={3}>
                <Stack spacing={1}>
                  <Typography variant="h5" noWrap>
                    {selectedVariant ? toDollar(selectedVariant.price * packCount + onMetalCost + configCost + customizationCost) : '-'}
                  </Typography>
                  {selectedVariant && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {toDollar(selectedVariant.unit_price + configUnitSurcharge)} per unit ·{' '}
                        {(selectedVariant.quantity * packCount).toLocaleString()} tags
                        {onMetalCost > 0 && ` · +${toDollar(onMetalCost)} on-metal`}
                        {customizationCost > 0 && ` · +${toDollar(customizationCost)} customization (one-time)`}
                      </Typography>
                      {savingsPct !== null && savingsPct > 0 && (
                        <Typography
                          variant="body3"
                          sx={{
                            color: theme.palette.success?.main ?? theme.palette.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          Save {savingsPct}% per unit
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
                <Stack direction="row" spacing={3} alignItems="center">
                  {/* Quantity multiplier — only on the top tier, to order beyond the
                      largest preset (the tiers already cover the normal range). */}
                  {isMaxTierSelected && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{
                        flexShrink: 0,
                        height: 40,
                        px: 0.5,
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 2,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label="Decrease quantity"
                        onClick={() => setPackCount(n => Math.max(1, n - 1))}
                        disabled={packCount <= 1}
                      >
                        <MinusIcon size={16} strokeWidth={1.5} />
                      </IconButton>
                      <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center' }}>
                        {packCount}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Increase quantity"
                        onClick={() => setPackCount(n => n + 1)}
                      >
                        <PlusIcon size={16} strokeWidth={1.5} />
                      </IconButton>
                    </Stack>
                  )}
                  {/* VRG-119: purchasing is gated to Admin/Finance/Purchasing — viewing
                      the shop stays open. disable+tooltip (§27) since these are in-context
                      actions on a surface the user can already see. */}
                  <PermissionGate
                    permission="tags_shop_purchase"
                    tooltip="You need the “Purchase tags” permission. Ask an admin to grant it."
                  >
                    <Button
                      disableElevation
                      sx={{ minWidth: 140 }}
                      onClick={handleAddToCart}
                      disabled={!selectedVariant || offerComingSoon}
                    >
                      Add to cart
                    </Button>
                  </PermissionGate>
                  <PermissionGate
                    permission="tags_shop_purchase"
                    tooltip="You need the “Purchase tags” permission. Ask an admin to grant it."
                  >
                    <Button
                      disableElevation
                      variant="secondary"
                      sx={{ minWidth: 140 }}
                      onClick={handleBuyNow}
                      disabled={!selectedVariant || offerComingSoon}
                    >
                      Buy Now
                    </Button>
                  </PermissionGate>
                </Stack>
                {/* VRG-630 — a coming-soon FORM has no badge to hang an explanation off.
                    The capability case is self-explaining: the greyed leaf badge carries
                    a ComingSoonNote tooltip, and another rung of the ladder is orderable.
                    Here every variant is blocked and the capability badges all read as
                    available, so a bare disabled button would be a dead end with no stated
                    cause (§27: a gate is only useful if it is actionable). Stated in the
                    flow rather than in a tooltip, because there is nothing to hover. */}
                {formComingSoon && (
                  <Typography variant="body3" color="text.secondary">
                    {comingSoonReason(product.title)}
                  </Typography>
                )}
                {/* Base lead-time — every order ships in ~4 weeks (customization adds no
                    extra time; the CustomizeTagModal states the same). VRG-156 item 3. */}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ClockIcon size={14} strokeWidth={1.5} color={theme.palette.text.secondary} />
                  <Typography variant="body3" color="text.secondary">
                    Ships in ~4 weeks.
                  </Typography>
                </Stack>
              </Stack>
              {/* Assigned products — Vera products using this tag as their default.
                  UI parity with the product page's Set Default Tag card (inverse).
                  Sits as its own section in the spacing={6} column, so its gap above
                  (from the price/CTA block) matches its gap below (to the accordions). */}
              {assignedProducts.length > 0 ? (
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Assigned products
                      </Typography>
                      <Button
                        variant="secondary"
                        disableElevation
                        startIcon={<ShoppingBagIcon strokeWidth={1} size={16} />}
                        onClick={() => setOpenAssignModal(true)}
                      >
                        Assign
                      </Button>
                    </Stack>
                    {/* Show ~3 rows then scroll, so a long list can't push the page. */}
                    <Stack
                      spacing={2}
                      sx={{ maxHeight: 264, overflowY: 'auto', pr: assignedProducts.length > 3 ? 1 : 0 }}
                    >
                      {assignedProducts.map((p) => (
                        <AssignedProductRow
                          key={p.id}
                          product={p}
                          onRemove={() => removeDefaultTag({ product_id: p.id })}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      p: 5,
                      // Assign card = background.paper (#0f0f12 in dark).
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.primary[100]}`,
                      borderRadius: 2,
                    }}
                  >
                    {/* Text left, action right in ONE row — parity with the product
                        page's "Set Default Tag" empty-state card. */}
                    <Stack
                      direction="row"
                      spacing={4}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack spacing={1} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Assign products
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                          Assign this tag to your products to make it their default. This enables direct ordering and inventory alerts.
                        </Typography>
                      </Stack>
                      <Button
                        variant="secondary"
                        disableElevation
                        startIcon={<ShoppingBagIcon strokeWidth={1} />}
                        onClick={() => setOpenAssignModal(true)}
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        Assign
                      </Button>
                    </Stack>
                  </Box>
                )}
              <Stack spacing={4}>
                <Accordion
                  defaultExpanded
                  disableGutters
                  sx={{
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.primary[100]}`,
                    // 2 (=8px), matching the "Assign products" card directly above.
                    // Was 3 (=12px): same fill and same border colour, but a different
                    // corner radius still reads as two different cards.
                    borderRadius: 2,
                    // A CARD, so it takes the card surface — same as the "Assign products"
                    // card directly above it. This was background.default on the reasoning
                    // that "accordions = background.default (#09090b in dark)", but that is
                    // a rule for accordions nested INSIDE a card (settings sections), where
                    // recessing them reads as depth. Here the accordion IS the card, so the
                    // recess just made two adjacent cards different colours — grey vs white
                    // in light mode, and two near-blacks in dark.
                    backgroundColor: theme.palette.background.paper,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                    // MUI squares 2 corners on the first/last accordion in a group —
                    // force all four to the same radius.
                    '&:first-of-type, &:last-of-type': { borderRadius: 2 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown strokeWidth={1} />}
                    aria-controls="product-description"
                    id="product-description"
                    sx={{
                      flexDirection: 'row-reverse',
                      px: 4,
                      minHeight: 56,
                      borderBottom: 'none',
                      '& .MuiAccordionSummary-content': { margin: 0 },
                      // Chevron sits flush-left at the same inset as the body text below.
                      '& .MuiAccordionSummary-expandIconWrapper': { mr: 2 },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                  </AccordionSummary>
                  {/* pl:7 nudges the body copy right to sit under the chevron glyph
                      (chevron centre ≈ summary px:4 + half the 24px icon), not its left edge. */}
                  <AccordionDetails sx={{ pl: 7, pr: 4, pt: 0, pb: 5 }}>
                    {product?.description && (
                      <div
                        dangerouslySetInnerHTML={{ __html: product.description }}
                        style={{ lineHeight: 1.6, color: theme.palette.text.primary }}
                      />
                    )}
                    {/* Dynamic composition — capability effect + active add-on notes (VRG-156). */}
                    {descriptionNotes.length > 0 && (
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ mt: product?.description ? 2 : 0, lineHeight: 1.6 }}
                      >
                        {descriptionNotes.join(' ')}
                      </Typography>
                    )}
                    {!product?.description && descriptionNotes.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No description available
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        {/* Only render the section when there are real, related products (VRG-156). */}
        {similar_products.length > 0 && (
          <>
            <Stack spacing={6} mt={10} mb={6}>
              <Typography variant="h2">Similar tags</Typography>
            </Stack>
            <Grid container spacing={3}>
              {similar_products.map(p => (
                <Grid
                  key={p.id}
                  item
                  md={3}
                  sx={{ cursor: 'pointer', minHeight: 350 }}
                  onClick={() => navigate(ROUTES.TAGS_SHOP_PRODUCT(p.sku), { state: referrer })}
                >
                  <TagsShopProductPreview product={p} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </>
      {product && (
        <SetProductsForTagModal
          open={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
          tagShopProductId={product.id}
          refetch={() => {}}
        />
      )}

      <CustomizeTagModal
        open={openCustomize}
        onClose={() => setOpenCustomize(false)}
        value={customization}
        canvasWidthMm={canvasFromSize?.widthMm ?? product?.width}
        canvasHeightMm={canvasFromSize?.heightMm ?? product?.height}
        canvasShape={canvasFromSize?.shape ?? product?.shape}
        canvasSides={previewSides}
        embroidered={isFabricForm(form)}
        isWoven={isFabricForm(form)}
        savedLogos={savedLogos}
        onUploadLogo={(dataUrl, name) => saveLogo(dataUrl, name)}
        onSave={(c) => {
          setCustomization(c)
          setOpenCustomize(false)
          // Save a reusable design for this tag form (placement is size/shape-specific).
          if (form) saveDesign(`${product?.title ?? form} design`, form, c)
        }}
        onRemove={() => {
          setCustomization(null)
          setOpenCustomize(false)
        }}
      />

      {/* Saved-designs dropdown for the Customization button (VRG-156) — apply a design
          previously saved for this tag form, or start a new customization. */}
      <Menu
        anchorEl={customizeMenuAnchor}
        open={!!customizeMenuAnchor}
        onClose={() => setCustomizeMenuAnchor(null)}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        // House paper chrome — the 8px gap between the button and the menu, the 4px
        // inset, the matching list inset, and the viewport-safe max height. The gap
        // used to be restated here as `mt: 1`, which rendered 4px on this app's 4px
        // spacing base; it is MENU_PAPER_SX's now, in absolute px.
        slotProps={{ paper: { sx: MENU_PAPER_SX } }}
      >
        {myDesigns.map(d => (
          <MenuItem
            key={d.id}
            onClick={() => {
              setCustomization(d.customization)
              setCustomizeMenuAnchor(null)
            }}
            sx={{ ...MENU_ITEM_SX }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {d.customization.logoDataUrl && (
                <Box component="img" src={d.customization.logoDataUrl} sx={{ width: 22, height: 22, objectFit: 'contain', borderRadius: 1 }} />
              )}
              <Typography variant="body3">{d.name}</Typography>
            </Stack>
          </MenuItem>
        ))}
        {/* "New customization" sits BENEATH a divider (a distinct action), plain text. */}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => { setCustomizeMenuAnchor(null); setOpenCustomize(true) }}
          sx={{ ...MENU_ITEM_SX, fontSize: '0.875rem' }}
        >
          + New customization
        </MenuItem>
      </Menu>

      {/* VRG-145 — capability guide */}
      <Modal
        title="Capabilities"
        open={openCapabilityGuide}
        onClose={() => setOpenCapabilityGuide(false)}
      >
        <Stack spacing={5}>
          {/* Intro: Connect is the baseline; capabilities layer on and can combine. */}
          <Stack spacing={2}>
            <TagCapabilityBadges leaves={['connect']} size="md" />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Every Vera tag has Connect, the baseline: a tap opens a digital experience.
              From there you add capabilities. They can be combined, and each one adds
              protection (and affects the price).
            </Typography>
          </Stack>
          {/* One badge per capability. */}
          {CAPABILITY_GUIDE.map(({ leaf, blurb }) => (
            <Stack key={leaf} spacing={2}>
              <TagCapabilityBadges leaves={[leaf]} size="md" />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {blurb}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Modal>
    </>
  )
}
